if (!customElements.get('sh-cart')) customElements.define('sh-cart', class extends (window.StandardEvents?.createViewEventElement?.(HTMLElement) || HTMLElement) {
  connectedCallback() {
    super.connectedCallback?.();
    this.root = window.Shopify?.routes?.root || '/';
    this.destination = null;
    this.rates = [];
    this.selectedRate = null;
    this.mutating = !!this.constructor.owner;
    this.storageKey = 'sh-cart-country-v2-' + location.hostname + '-' + this.dataset.currency;
    try { this.destination = JSON.parse(sessionStorage.getItem(this.storageKey)); } catch {}
    this.onClick = this.click.bind(this);
    this.onChange = this.change.bind(this);
    this.onSubmit = this.submit.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    this.init();
    if (this.constructor.owner) this.applyBusy(true);
    else if (this.dataset.mode === 'page' || !document.querySelector('sh-cart[data-mode="page"]')) this.ready = this.verifyGiftsOnLoad();
  }
  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.summaryObserver?.disconnect();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit);
    clearTimeout(this.quantityTimer);
  }
  init() {
    const config = this.querySelector('[data-cart-config]');
    if (!config) return;
    this.config = JSON.parse(config.textContent);
    this.cart = this.config.cart;
    this.copy = this.config.copy;
    try { this.markets = JSON.parse(this.config.markets || '{}'); } catch { this.markets = {}; }
    this.formatter = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.cart.currency });
    try { this.shippingRules = JSON.parse(this.config.shippingRates || '{}'); } catch { this.shippingRules = {}; }
    try { this.exchangeRates = JSON.parse(this.config.exchangeRates || '{}'); } catch { this.exchangeRates = {}; }
    try { this.giftRules = JSON.parse(this.config.giftRules || '{}'); } catch { this.giftRules = {}; }
    try { this.orderReward = JSON.parse(this.config.orderReward || 'null'); } catch { this.orderReward = null; }
    const countrySelect = this.querySelector('[data-country-select]');
    if (countrySelect) {
      try {
        const countryNames = new Intl.DisplayNames([this.dataset.locale], { type: 'region' });
        for (const option of countrySelect.options) option.textContent = countryNames.of(option.value);
      } catch {}
      const domains = { 'sportshub.es': 'ES', 'sportshub.fr': 'FR', 'de.sportshubeurope.com': 'DE', 'nl.sportshubeurope.com': 'NL', 'pt.sportshubeurope.com': 'PT', 'it.sportshubeurope.com': 'IT', 'no.sportshubeurope.com': 'NO' };
      const country = this.destination?.country || domains[location.hostname.replace(/^www\./, '')] || this.dataset.country;
      if ([...countrySelect.options].some(o => o.value === country)) countrySelect.value = country;
      this.destination = { country: countrySelect.value };
    }
    this.renderRewards();
    this.renderRecommendations();
    this.renderGifts();
    this.summaryObserver?.disconnect();
    const checkout = this.querySelector('.shc-summary > button');
    const sticky = this.querySelector('.shc-mobile-total');
    if (checkout && sticky && this.dataset.mode !== 'drawer') {
      this.summaryObserver = new IntersectionObserver(entries => { sticky.hidden = entries[0].isIntersecting; }, { threshold: 0.5 });
      this.summaryObserver.observe(checkout);
    }
    if (countrySelect && this.cart.item_count) this.calculateRates();
    this.closest('cart-drawer')?.classList.toggle('is-empty', !this.cart.item_count);
  }
  async activate() {
    if (this.mutating) return this.ready;
    this.setBusy(true);
    try {
      const checked = await this.reconcileRewards(await this.json(this.root + 'cart.js'));
      if (checked.result || this.cartSignature(checked.cart) !== this.cartSignature(this.cart)) await this.refresh(checked.result);
      else { this.cart = checked.cart; this.renderGifts(); }
      this.giftUnsafe = false;
      if (checked.removed) this.status(this.t('gift_removed'));
    } catch { this.giftUnsafe = true; this.status(this.t('error'), true); }
    finally { this.setBusy(false); }
  }
  money(cents) { return this.formatter.format(cents / 100); }
  t(key, values = {}) { return Object.entries(values).reduce((s, [k, v]) => s.replaceAll('{' + k + '}', String(v)), this.copy[key] || ''); }
  status(message = '', error = false) {
    const el = this.querySelector('[data-status]');
    if (el) { el.textContent = message; el.classList.toggle('is-error', error); }
  }
  async json(url, options = {}) {
    const { transactionStep = false, responseType = 'json', ...settings } = options;
    const request = { ...settings, headers: { Accept: 'application/json', ...settings.headers } };
    const readOnly = !request.method || request.method.toUpperCase() === 'GET';
    for (let attempt = 0; ; attempt++) {
      const controller = readOnly ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), 8000) : null;
      let response;
      try {
        const init = { ...request, ...(controller ? { signal: controller.signal, cache: 'no-store' } : {}) };
        response = transactionStep ? await fetch(new Request(new URL(url, location.href), init)) : await fetch(url, init);
        const data = responseType === 'text' ? await response.text() : await response.json();
        if (!response.ok || data?.status >= 400) throw new Error(typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : this.t('error'));
        return data;
      } catch (error) {
        // Only reads can be repeated: a failed POST may already have changed the cart.
        if (!readOnly || attempt || (response && ![408, 409, 429, 500, 502, 503, 504].includes(response.status))) throw error;
        await new Promise(resolve => setTimeout(resolve, 200));
      } finally { clearTimeout(timer); }
    }
  }
  async mutation(endpoint, body, focusKey) {
    if (this.mutating) return;
    const active = document.activeElement;
    const restoreInput = active?.matches('input[data-key]');
    const restoreStep = active?.dataset?.step;
    this.setBusy(true);
    this.status();
    try {
      let result = await this.requestCart(endpoint, body);
      const cart = result.token ? result : await this.json(this.root + 'cart.js');
      const checked = await this.reconcileRewards(cart);
      result = checked.result || result;
      await this.refresh(result);
      if (focusKey && restoreInput) this.querySelector('input[data-key="' + CSS.escape(focusKey) + '"]')?.focus({ preventScroll: true });
      else if (focusKey && restoreStep) this.querySelector('[data-line-key="' + CSS.escape(focusKey) + '"] [data-step="' + restoreStep + '"]')?.focus({ preventScroll: true });
      this.status(checked.removed ? this.t('gift_removed') : endpoint === 'add' ? this.t('added') : '');
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sh-cart', cartData: this.cart });
      if (!this.giftItems().length) document.dispatchEvent(new CustomEvent('bndlr:cart_was_modified'));
    } catch (error) {
      try {
        const checked = await this.reconcileRewards(await this.json(this.root + 'cart.js'));
        await this.refresh(checked.result);
      } catch { this.giftUnsafe = true; }
      this.status(error.message || this.t('error'), true);
    } finally { this.setBusy(false); }
  }
  setBusy(busy) {
    if (busy) this.constructor.owner = this;
    else if (this.constructor.owner === this) this.constructor.owner = null;
    document.querySelectorAll('sh-cart').forEach(cart => { if (cart.config) cart.applyBusy(busy); });
  }
  applyBusy(busy) {
    this.mutating = busy;
    this.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('button,input[data-key],[data-country-select]').forEach(el => el.disabled = busy);
    if (!busy) this.renderGifts();
  }
  requestCart(endpoint, body, renderSections = true) {
    return this.json(this.root + 'cart/' + endpoint + '.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      transactionStep: true,
      body: JSON.stringify({ ...body, ...(renderSections ? { sections: [...new Set([...document.querySelectorAll('sh-cart')].map(cart => cart.dataset.section).concat('cart-icon-bubble'))], sections_url: location.pathname } : {}) })
    });
  }
  async refresh(result) {
    let sections = result?.sections;
    const carts = [...document.querySelectorAll('sh-cart')];
    const sectionIds = [...new Set(carts.map(cart => cart.dataset.section).concat('cart-icon-bubble'))];
    const readSections = async () => {
      const url = new URL(location.pathname, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('sections', sectionIds.join(','));
      return this.json(url.href, { headers: { Accept: 'text/html' } });
    };
    // A rendering failure must not trigger another cart mutation or discount recalculation.
    if (carts.some(cart => !sections?.[cart.dataset.section])) sections = await readSections();
    let parsed = new Map(carts.map(cart => [cart, new DOMParser().parseFromString(sections?.[cart.dataset.section] || '', 'text/html')]));
    const nextIds = new Map();
    if ([...parsed.values()].some(doc => !doc.querySelector('[data-cart-body]'))) {
      sections ||= {};
      // A page restored after theme publication can retain section IDs from the old theme.
      const url = new URL(this.root + 'cart', location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const fresh = new DOMParser().parseFromString(await this.json(url.href, { headers: { Accept: 'text/html' }, responseType: 'text' }), 'text/html');
      parsed = new Map(carts.map(cart => {
        const next = fresh.querySelector('sh-cart[data-mode="' + cart.dataset.mode + '"]');
        if (!next?.querySelector('[data-cart-body]') || !next.dataset.section) throw new Error(this.t('error'));
        nextIds.set(cart, next.dataset.section);
        return [cart, new DOMParser().parseFromString(next.outerHTML, 'text/html')];
      }));
      const bubble = fresh.getElementById('cart-icon-bubble');
      if (bubble) sections['cart-icon-bubble'] = '<div class="shopify-section">' + bubble.innerHTML + '</div>';
    }
    const scroll = window.scrollY;
    for (const cart of carts) {
      const doc = parsed.get(cart);
      const nextBody = doc.querySelector('[data-cart-body]');
      if (nextIds.has(cart)) cart.dataset.section = nextIds.get(cart);
      const innerScroll = cart.querySelector('[data-cart-scroll]')?.scrollTop || 0;
      cart.giftsOpen = cart.querySelector('[data-gift-panel]')?.open;
      cart.querySelector('[data-cart-body]').replaceWith(nextBody);
      const payload = doc.querySelector('sh-cart')?.getAttribute('view-event-payload');
      if (payload) cart.setAttribute('view-event-payload', payload);
      cart.destination = this.destination;
      cart.init();
      cart.closest('cart-drawer')?.updateNoticeContent?.(doc);
      cart.applyBusy(this.mutating);
      if (cart.dataset.mode === 'drawer') cart.querySelector('[data-cart-scroll]').scrollTop = innerScroll;
    }
    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.innerHTML = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section')?.innerHTML || sections['cart-icon-bubble'];
    }
    if (window.scrollY !== scroll) window.scrollTo({ top: scroll, behavior: 'instant' });
  }
  giftItems(cart = this.cart) { return cart.items.filter(i => i.properties?._sh_reward === 'gift-v1'); }
  giftRule() {
    return this.config.previewRewards ? this.giftRules[this.dataset.country] : null;
  }
  giftSpend(cart = this.cart) { return cart.items.filter(i => i.properties?._sh_reward !== 'gift-v1').reduce((sum, i) => sum + i.final_line_price, 0); }
  cartSignature(cart) { return JSON.stringify([cart.total_price, cart.currency, cart.items.map(item => [item.key, item.quantity, item.final_line_price])]); }
  orderRule() { return this.config.previewRewards ? this.orderReward : null; }
  orderThreshold() { const rule = this.orderRule(); return rule ? this.convert(rule.minimum, rule.currency) : null; }
  orderEligible(cart = this.cart) { const minimum = this.orderThreshold(); return minimum !== null && this.giftSpend(cart) >= minimum; }
  orderDiscount(cart = this.cart) {
    const rule = this.orderRule();
    const title = rule?.code?.trim().toUpperCase();
    return title && (cart.cart_level_discount_applications || []).find(entry => {
      const discount = entry.discount_application || entry;
      return discount.title?.trim().toUpperCase() === title && (entry.amount || discount.total_allocated_amount) > 0;
    });
  }
  legacyEgoOrderAttribute(cart, rule = this.orderRule()) {
    const current = String(cart.attributes?.EgoFreeGift || '');
    const legacyIds = new Set((rule?.legacyEgoCampaignIds || []).map(String));
    if (!current || !legacyIds.size) return { current, value: current };
    let entries;
    try {
      const parsed = JSON.parse(current);
      entries = Array.isArray(parsed) ? parsed : [parsed];
      if (entries.some(entry => !entry || typeof entry !== 'object' || Array.isArray(entry))) throw new Error('Unsupported entry');
    } catch {
      // An unrecognised value can belong to another app. Never replace it blindly.
      return { current, value: current };
    }
    const retained = entries.filter(entry => !legacyIds.has(String(entry.campaignId)));
    if (retained.length === entries.length) return { current, value: current };
    return { current, value: retained.length === 0 ? '' : JSON.stringify(retained.length === 1 ? retained[0] : retained) };
  }
  async syncOrderReward(cart) {
    const rule = this.orderRule();
    if (!rule) return { cart };
    const codes = (cart.discount_codes || []).map(d => d.code);
    const legacyCodes = new Set((rule.legacyCodes || []).map(code => code.toUpperCase()));
    const legacyPresent = codes.some(code => legacyCodes.has(code.toUpperCase()));
    const present = codes.some(code => code.toUpperCase() === rule.code.toUpperCase());
    const eligible = this.orderEligible(cart);
    const attribute = this.legacyEgoOrderAttribute(cart, rule);
    const attributesChanged = attribute.value !== attribute.current;
    const codesChanged = present !== eligible || legacyPresent;
    if (!codesChanged && !attributesChanged) {
      if (eligible && !this.orderDiscount(cart) && !(cart.cart_level_discount_applications || []).length) throw new Error(this.t('error'));
      return { cart };
    }
    const next = codes.filter(code => code.toUpperCase() !== rule.code.toUpperCase() && !legacyCodes.has(code.toUpperCase()));
    if (eligible) next.push(rule.code);
    const result = await this.requestCart('update', {
      ...(codesChanged ? { discount: next.join(',') } : {}),
      ...(attributesChanged ? { attributes: { EgoFreeGift: attribute.value } } : {})
    });
    if (eligible && !this.orderDiscount(result) && !(result.cart_level_discount_applications || []).length) throw new Error(this.t('error'));
    return { cart: result, result };
  }
  async reconcileRewards(cart) {
    const order = await this.syncOrderReward(cart);
    const gift = await this.reconcileGifts(order.cart);
    const final = gift.result ? await this.syncOrderReward(gift.cart) : { cart: gift.cart };
    return { cart: final.cart, result: final.result || gift.result || order.result, removed: gift.removed };
  }
  giftThreshold() {
    const rule = this.giftRule();
    return rule ? this.convert(rule.minimum, rule.currency) : null;
  }
  giftEligible(cart = this.cart) {
    const threshold = this.giftThreshold();
    return threshold !== null && this.giftSpend(cart) >= threshold;
  }
  giftCodes() { return new Set(Object.values(this.giftRules).flatMap(r => [...Object.values(r.codes || {}), ...(r.legacyCodes || [])]).map(s => s.toUpperCase())); }
  otherCodes(cart) { const own = this.giftCodes(); return (cart.discount_codes || []).map(d => d.code).filter(c => !own.has(c.toUpperCase())); }
  automaticGiftRule(rule = this.giftRule()) { return rule?.provider === 'ego' || !!rule?.promotionId; }
  allowedGift(item, rule = this.giftRule()) {
    if (!item || !rule) return false;
    const productId = String(item.product_id);
    const variant = rule.giftVariants?.[productId] || (rule.provider !== 'ego' && this.querySelector('[data-gift="' + productId + '"]')?.dataset.variant);
    return Number.isSafeInteger(Number(variant)) && Number(variant) > 0 && Number(variant) === Number(item.variant_id);
  }
  giftProperties(properties = {}) {
    const rule = this.giftRule();
    const clean = { ...properties };
    for (const key of ['_promotion_id', '_promotion_source', '_nexus_free_gift', '_Ego', '_EgoFreeGift']) delete clean[key];
    if (rule?.provider === 'ego') {
      if (!rule.campaignId) throw new Error(this.t('gift_error'));
      return { ...clean, _sh_reward: 'gift-v1', _EgoFreeGift: rule.campaignId, _Ego: rule.campaignId };
    }
    return { ...clean, _sh_reward: 'gift-v1', ...(rule?.promotionId ? {
      _promotion_id: rule.promotionId, _promotion_source: 'promotions_app', _nexus_free_gift: '1'
    } : {}) };
  }
  async addRewardGift(variantId, properties, cart) {
    const rule = this.giftRule();
    const automatic = this.automaticGiftRule(rule);
    const added = await this.requestCart('add', { items: [{ id: Number(variantId), quantity: 1, properties: this.giftProperties(properties) }] }, automatic);
    if (automatic) return { ...await this.json(this.root + 'cart.js'), sections: added.sections };
    const productId = added.items?.[0]?.product_id;
    return this.requestCart('update', { discount: [...this.otherCodes(cart), rule.codes[String(productId)]].join(',') });
  }
  async restoreRewardGift(item, cart) {
    const rule = this.giftRule();
    const legacy = rule?.provider === 'ego' && (rule.legacyPromotionIds || []).includes(item.properties?._promotion_id) && item.properties?._nexus_free_gift === '1';
    if (!legacy) return this.addRewardGift(item.variant_id, item.properties, cart);
    // Rollback restores the previously valid unit, including its original provider.
    const added = await this.requestCart('add', { items: [{ id: item.variant_id, quantity: 1, properties: { ...item.properties, _sh_reward: 'gift-v1' } }] });
    return { ...await this.json(this.root + 'cart.js'), sections: added.sections };
  }
  freeGift(item, cart = this.cart) {
    const rule = this.giftRule();
    if (!this.allowedGift(item, rule) || item.properties?._sh_reward !== 'gift-v1' || item.quantity !== 1 || item.final_line_price !== 0 || !this.giftEligible(cart)) return false;
    const allocated = titles => (item.line_level_discount_allocations || []).some(d => d.amount > 0 && titles.some(title => title && d.discount_application?.title?.trim().toUpperCase() === title.trim().toUpperCase()));
    if (rule.provider === 'ego') {
      const current = item.properties._Ego === rule.campaignId && item.properties._EgoFreeGift === rule.campaignId;
      const legacy = (rule.legacyPromotionIds || []).includes(item.properties._promotion_id) && item.properties._nexus_free_gift === '1';
      return (current && allocated(rule.discountTitles || [])) || (legacy && allocated(rule.legacyDiscountTitles || []));
    }
    const titles = rule?.discountTitles || [rule?.codes?.[String(item.product_id)]];
    const marked = !rule?.promotionId || ((rule.promotionIds || [rule.promotionId]).includes(item.properties?._promotion_id) && item.properties?._nexus_free_gift === '1');
    return marked && allocated(titles);
  }
  validGiftSelection(previous, cart, productId) {
    const gifts = this.giftItems(cart);
    const codeLost = (previous.discount_codes || []).some(d => d.applicable && !this.giftCodes().has(d.code.toUpperCase()) && !(cart.discount_codes || []).some(n => n.code.toUpperCase() === d.code.toUpperCase() && n.applicable));
    return gifts.length === 1 && gifts[0].product_id === Number(productId) && this.freeGift(gifts[0], cart) && !codeLost && this.paidContents(cart) === this.paidContents(previous) && this.giftSpend(cart) <= this.giftSpend(previous) + 1;
  }
  legacyGift(item, rule = this.giftRule()) {
    return rule?.provider === 'ego' && item?.properties?._sh_reward === 'gift-v1' && (rule.legacyPromotionIds || []).includes(item.properties._promotion_id) && item.properties._nexus_free_gift === '1';
  }
  migrationPaidUnchanged(previous, cart) {
    const canonicalCodes = source => [...new Set(this.otherCodes(source).map(code => code.toUpperCase()))].sort().join(',');
    return this.paidContents(cart) === this.paidContents(previous) && this.giftSpend(cart) <= this.giftSpend(previous) && canonicalCodes(cart) === canonicalCodes(previous);
  }
  validGiftMigration(previous, cart, original, requireEgo = true) {
    const gifts = this.giftItems(cart);
    if (!this.validGiftSelection(previous, cart, original.product_id) || !this.migrationPaidUnchanged(previous, cart) || gifts[0].variant_id !== original.variant_id) return false;
    const item = gifts[0];
    const providerKeys = new Set(['_sh_reward', '_promotion_id', '_promotion_source', '_nexus_free_gift', '_Ego', '_EgoFreeGift']);
    if (Object.entries(original.properties || {}).some(([key, value]) => !providerKeys.has(key) && JSON.stringify(item.properties?.[key]) !== JSON.stringify(value))) return false;
    if (!requireEgo) return true;
    const rule = this.giftRule();
    return item.properties._Ego === rule.campaignId && item.properties._EgoFreeGift === rule.campaignId && !item.properties._promotion_id && !item.properties._promotion_source && !item.properties._nexus_free_gift;
  }
  async changeGiftProperties(cart, item, properties) {
    const index = cart.items.indexOf(item);
    if (index < 0 || item.properties?._sh_reward !== 'gift-v1' || item.quantity !== 1 || !this.allowedGift(item)) throw new Error(this.t('gift_error'));
    let result;
    // A line key prevents a concurrent reorder from assigning gift properties to a paid copy.
    const identity = item.key ? { id: item.key } : { line: index + 1 };
    try {
      result = await this.requestCart('change', { ...identity, quantity: 1, properties });
    } catch {
      // A failed POST can already be committed. Read its actual result before rollback.
    }
    return { cart: await this.json(this.root + 'cart.js'), result };
  }
  async migrateLegacyGift(previous, original) {
    const wasValid = this.freeGift(original, previous);
    let changed = await this.changeGiftProperties(previous, original, this.giftProperties(original.properties));
    let current = changed.cart;
    if (this.validGiftMigration(previous, current, original)) {
      return { cart: current, result: { ...current, ...(changed.result?.sections ? { sections: changed.result.sections } : {}) }, removed: false, migrated: true };
    }
    const currentGifts = this.giftItems(current);
    if (currentGifts.length === 1 && this.legacyGift(currentGifts[0]) && this.validGiftMigration(previous, current, original, false)) {
      // The write did not commit; keep the still-free original rather than repeat it.
      return { cart: current, removed: false, migrated: false };
    }
    if (wasValid && currentGifts.length === 1 && currentGifts[0].variant_id === original.variant_id && currentGifts[0].quantity === 1 && this.allowedGift(currentGifts[0])) {
      changed = await this.changeGiftProperties(current, currentGifts[0], { ...original.properties, _sh_reward: 'gift-v1' });
      current = changed.cart;
      if (this.validGiftMigration(previous, current, original, false)) {
        return { cart: current, result: { ...current, ...(changed.result?.sections ? { sections: changed.result.sections } : {}) }, removed: false, migrated: false };
      }
    }
    // An expired/rejected promotion must never leave its marked unit chargeable.
    const result = await this.removeGiftLines(current);
    if (!this.migrationPaidUnchanged(previous, result)) throw new Error(this.t('gift_error'));
    return { cart: result, result, removed: true, migrated: false };
  }
  async reconcileGifts(cart) {
    const gifts = this.giftItems(cart);
    if (gifts.length === 1 && this.legacyGift(gifts[0]) && gifts[0].quantity === 1 && this.allowedGift(gifts[0]) && this.giftEligible(cart)) return this.migrateLegacyGift(cart, gifts[0]);
    const valid = gifts.length === 1 && this.freeGift(gifts[0], cart);
    if (this.automaticGiftRule()) {
      const legacy = (cart.discount_codes || []).some(d => this.giftCodes().has(d.code.toUpperCase()));
      if ((!gifts.length || valid) && !legacy) return { cart, removed: false };
      if (!valid) cart = await this.removeGiftLines(cart);
      const result = legacy ? await this.requestCart('update', { discount: this.otherCodes(cart).join(',') }) : cart;
      this.giftUnsafe = false;
      return { cart: result, result, removed: gifts.length > 0 && !valid };
    }
    const stray = !gifts.length && (cart.discount_codes || []).some(d => this.giftCodes().has(d.code.toUpperCase()));
    if ((!gifts.length || valid) && !stray) return { cart, removed: false };
    const gift = gifts[0];
    const code = gift && this.giftRule()?.codes[String(gift.product_id)];
    // Shopify can move Buy X Get Y onto a purchased copy of the same variant.
    const moved = gifts.length === 1 && gift.quantity === 1 && code && this.giftEligible(cart) && cart.items.some(item =>
      item.properties?._sh_reward !== 'gift-v1' && item.variant_id === gift.variant_id &&
      (item.line_level_discount_allocations || []).some(d => d.amount > 0 && d.discount_application.title.toUpperCase() === code.toUpperCase())
    );
    if (moved) {
      const paidContents = this.paidContents(cart);
      await this.removeGiftLines(cart);
      await this.requestCart('add', { items: [{ id: gift.variant_id, quantity: 1, properties: { ...gift.properties, _sh_reward: 'gift-v1' } }] }, false);
      const result = await this.requestCart('update', { discount: [...this.otherCodes(cart), code].join(',') });
      if (this.paidContents(result) !== paidContents) throw new Error(this.t('gift_error'));
      const repaired = this.giftItems(result);
      if (repaired.length === 1 && this.freeGift(repaired[0], result)) return { cart: result, result, removed: false };
      cart = result;
    }
    await this.removeGiftLines(cart);
    const result = await this.requestCart('update', { discount: this.otherCodes(cart).join(',') });
    this.giftUnsafe = false;
    return { cart: result, result, removed: gifts.length > 0 };
  }
  paidContents(cart) {
    const quantities = new Map();
    for (const item of cart.items.filter(i => i.properties?._sh_reward !== 'gift-v1')) {
      const identity = JSON.stringify([item.variant_id, Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)), item.selling_plan_allocation?.selling_plan?.id || null]);
      quantities.set(identity, (quantities.get(identity) || 0) + item.quantity);
    }
    return JSON.stringify([...quantities].sort(([a], [b]) => a.localeCompare(b)));
  }
  async removeGiftLines(cart) {
    for (let attempts = 0; this.giftItems(cart).length && attempts < 12; attempts++) {
      const item = this.giftItems(cart)[0];
      cart = await this.requestCart('change', { ...(item.key ? { id: item.key } : { line: cart.items.indexOf(item) + 1 }), quantity: 0 }, false);
    }
    if (this.giftItems(cart).length) throw new Error(this.t('gift_error'));
    return cart;
  }
  async verifyGiftsOnLoad() {
    if (this.mutating) return;
    if (!this.giftItems().length && !this.orderEligible() && !this.orderDiscount()) return;
    if (!this.giftRule() && !this.orderRule()) return;
    this.setBusy(true);
    try {
      const checked = await this.reconcileRewards(await this.json(this.root + 'cart.js'));
      if (checked.result || this.cartSignature(checked.cart) !== this.cartSignature(this.cart)) await this.refresh(checked.result);
      else { this.cart = checked.cart; this.renderGifts(); }
      this.giftUnsafe = false;
      if (checked.removed) this.status(this.t('gift_removed'));
    } catch { this.giftUnsafe = true; this.status(this.t('error'), true); }
    finally { this.setBusy(false); }
  }
  async submit(event) {
    if (this.checkoutReady || !event.submitter?.matches('[name=checkout]')) return;
    if (!this.giftItems().length && !this.giftUnsafe && !this.orderRule()) return;
    event.preventDefault();
    if (this.mutating) return;
    this.setBusy(true);
    try {
      const checked = await this.reconcileRewards(await this.json(this.root + 'cart.js'));
      this.giftUnsafe = false;
      if (checked.removed) { await this.refresh(checked.result); this.status(this.t('gift_removed')); }
      else {
        if (checked.result) await this.refresh(checked.result);
        this.setBusy(false);
        this.checkoutReady = true;
        this.querySelector('[data-cart-form]').requestSubmit(this.querySelector('[name=checkout]'));
      }
    } catch { this.status(this.t('error'), true); }
    finally { this.setBusy(false); this.checkoutReady = false; }
  }
  async chooseGift(button) {
    if (this.mutating || !this.giftEligible()) return;
    const productId = button.dataset.gift;
    const rule = this.giftRule();
    if (!this.allowedGift({ product_id: Number(productId), variant_id: Number(button.dataset.variant) }, rule) || (!this.automaticGiftRule(rule) && !rule?.codes?.[productId]) || button.dataset.available !== 'true') return;
    this.setBusy(true);
    this.querySelector('[data-gift-card="' + productId + '"]')?.classList.add('is-pending');
    button.querySelector('[data-gift-state]').textContent = this.t('adding');
    this.status();
    let previous;
    let oldGift;
    let touched = false;
    try {
      previous = await this.json(this.root + 'cart.js');
      const checked = await this.reconcileRewards(previous);
      previous = checked.cart;
      if (checked.result) await this.refresh(checked.result);
      if (!this.giftEligible(previous)) throw new Error(this.t('gift_error'));
      oldGift = this.giftItems(previous)[0];
      if (oldGift?.product_id === Number(productId) && this.freeGift(oldGift, previous)) return;
      touched = true;
      await this.removeGiftLines(previous);
      if ((!this.automaticGiftRule(rule) && oldGift) || (previous.discount_codes || []).some(d => this.giftCodes().has(d.code.toUpperCase()))) {
        await this.requestCart('update', { discount: this.otherCodes(previous).join(',') }, false);
      }
      const result = await this.addRewardGift(button.dataset.variant, {}, previous);
      if (!this.validGiftSelection(previous, result, productId)) throw new Error(this.t('gift_error'));
      this.giftUnsafe = false;
      await this.refresh(result);
      const renderedGifts = this.giftItems();
      if (renderedGifts.length !== 1 || renderedGifts[0].product_id !== Number(productId) || !this.freeGift(renderedGifts[0])) throw new Error(this.t('gift_error'));
      const confirmed = await this.json(this.root + 'cart.js');
      const confirmedGifts = this.giftItems(confirmed);
      if (confirmedGifts.length !== 1 || confirmedGifts[0].product_id !== Number(productId) || !this.freeGift(confirmedGifts[0], confirmed)) throw new Error(this.t('gift_error'));
      this.cart = confirmed;
      this.status(this.t('gift_in_cart'));
      this.querySelector('[data-gift="' + productId + '"]')?.focus({ preventScroll: true });
    } catch {
      if (!touched) { this.status(this.t('gift_error'), true); return; }
      try {
        const current = await this.json(this.root + 'cart.js');
        if (this.validGiftSelection(previous, current, productId)) {
          // The gift is committed. Recover its view without removing/re-adding it.
          this.giftUnsafe = false;
          try {
            await this.refresh();
            this.status(this.t('gift_in_cart'));
          } catch { this.status(this.t('error'), true); }
          return;
        }
        let result = await this.removeGiftLines(current);
        const desiredCodes = this.otherCodes(previous || current);
        const presentCodes = (result.discount_codes || []).map(d => d.code);
        const canonical = codes => [...new Set(codes.map(code => code.toUpperCase()))].sort().join(',');
        if (canonical(desiredCodes) !== canonical(presentCodes)) result = await this.requestCart('update', { discount: desiredCodes.join(',') }, false);
        if (oldGift && previous && this.freeGift(oldGift, previous) && this.giftEligible(result) && this.paidContents(result) === this.paidContents(previous)) {
          result = await this.restoreRewardGift(oldGift, result);
          if (!this.validGiftSelection(previous, result, oldGift.product_id)) {
            result = await this.removeGiftLines(result);
          }
        }
        this.giftUnsafe = false;
        await this.refresh(result);
      } catch { this.giftUnsafe = true; }
      this.status(this.t('gift_error'), true);
    } finally {
      this.querySelectorAll('.is-pending').forEach(el => el.classList.remove('is-pending'));
      this.setBusy(false);
    }
  }
  click(event) {
    const step = event.target.closest('[data-step]');
    const remove = event.target.closest('[data-remove]');
    const add = event.target.closest('[data-add]');
    const gift = event.target.closest('[data-gift]');
    if (step) {
      const input = step.closest('.shc-quantity').querySelector('input');
      const value = Math.max(0, Number(input.value) + Number(step.dataset.step));
      this.mutation('change', { id: input.dataset.key, quantity: value }, input.dataset.key);
    } else if (remove) {
      event.preventDefault();
      this.mutation('change', { id: remove.closest('[data-line-key]').dataset.lineKey, quantity: 0 });
    } else if (add) this.mutation('add', { items: [{ id: Number(add.dataset.add), quantity: 1 }] });
    else if (gift) this.chooseGift(gift);
  }
  change(event) {
    const input = event.target.closest('input[data-key]');
    if (input) {
      const value = Number(input.value);
      if (!Number.isInteger(value) || value < 0) { input.value = this.cart.items.find(i => i.key === input.dataset.key)?.quantity || 1; return; }
      clearTimeout(this.quantityTimer);
      this.quantityTimer = setTimeout(() => this.mutation('change', { id: input.dataset.key, quantity: value }, input.dataset.key), 180);
    }
    if (event.target.name === 'shipping-rate') {
      this.selectedRate = this.rates[Number(event.target.value)];
      this.renderTotals();
    }
    if (event.target.matches('[data-country-select]')) {
      this.destination = { country: event.target.value };
      try { sessionStorage.setItem(this.storageKey, JSON.stringify(this.destination)); } catch {}
      this.renderRewards();
      this.calculateRates();
      document.querySelectorAll('sh-cart').forEach(cart => {
        if (cart === this || !cart.config) return;
        cart.destination = this.destination;
        const select = cart.querySelector('[data-country-select]');
        if (select) select.value = event.target.value;
        cart.renderRewards();
        cart.calculateRates();
      });
    }
  }
  renderGifts() {
    const panel = this.querySelector('[data-gift-panel]');
    if (!panel) return;
    const eligible = this.giftEligible();
    const selected = this.giftItems().find(i => this.freeGift(i));
    if (eligible && this.wasGiftEligible !== true) panel.open = true;
    else if (this.giftsOpen !== undefined) panel.open = this.giftsOpen;
    this.giftsOpen = undefined;
    this.wasGiftEligible = eligible;
    const amount = Math.max(0, (this.giftThreshold() || 0) - this.giftSpend());
    this.querySelector('[data-gift-help]').textContent = this.t(selected ? 'gift_in_cart' : eligible ? 'gift_ready' : 'gift_locked_help', { amount: this.money(amount) });
    const rule = this.giftRule();
    this.querySelectorAll('[data-gift]').forEach(button => {
      const active = selected?.product_id === Number(button.dataset.gift);
      const configured = this.automaticGiftRule(rule) ? this.allowedGift({ product_id: Number(button.dataset.gift), variant_id: Number(button.dataset.variant) }, rule) : Boolean(rule?.codes?.[button.dataset.gift]);
      const available = button.dataset.available === 'true';
      button.setAttribute('aria-pressed', String(active));
      button.closest('.shc-gift-card').classList.toggle('is-selected', active);
      button.disabled = this.mutating || active || !available || !eligible || !configured;
      button.querySelector('[data-gift-state]').textContent = this.t(active ? 'chosen' : !available ? 'gift_unavailable' : eligible ? 'add_gift' : 'gift_locked');
    });
  }
  renderRewards() {
    const country = this.querySelector('[data-country-select]')?.value || this.dataset.country;
    const config = this.markets[country];
    const wrapper = this.querySelector('.shc-rewards');
    if (!wrapper) return;
    const valid = config && (country === 'NO' ? this.cart.currency === 'NOK' : this.cart.currency === 'EUR');
    wrapper.hidden = !valid;
    if (!valid) return;
    const proposed = this.config.previewRewards;
    const thresholds = (proposed ? config.slice(0, 3) : [config[0]]).map(n => Math.round(n * 100));
    if (proposed && this.giftThreshold() !== null) thresholds[1] = this.giftThreshold();
    if (proposed && this.orderThreshold() !== null) thresholds[2] = this.orderThreshold();
    const milestones = thresholds.map((amount, id) => ({ amount, id })).sort((a, b) => a.amount - b.amount);
    const spend = this.giftSpend();
    milestones.forEach(({ amount, id }, position) => {
      const label = this.querySelector('[data-threshold="' + id + '"]');
      if (label) label.textContent = this.money(amount);
      const reached = spend >= amount;
      const milestone = this.querySelector('[data-milestone="' + id + '"]');
      if (milestone) { milestone.classList.toggle('is-reached', reached); milestone.parentElement.append(milestone); }
      const checkpoint = this.querySelector('[data-checkpoint="' + id + '"]');
      if (checkpoint) {
        checkpoint.classList.toggle('is-reached', reached);
        checkpoint.style.left = ((position + 0.5) / milestones.length * 100) + '%';
      }
    });
    const next = milestones.findIndex(m => m.amount > spend);
    let percent = 100;
    if (next >= 0) {
      const previousAmount = next ? milestones[next - 1].amount : 0;
      const previousPosition = next ? (next - 0.5) / milestones.length * 100 : 0;
      const nextPosition = (next + 0.5) / milestones.length * 100;
      const fraction = Math.max(0, Math.min(1, (spend - previousAmount) / (milestones[next].amount - previousAmount)));
      percent = previousPosition + fraction * (nextPosition - previousPosition);
    }
    this.querySelector('[data-progress]').style.width = percent + '%';
    this.querySelector('[role=progressbar]').setAttribute('aria-valuenow', String(Math.round(percent)));
    this.querySelector('[data-reward-next]').textContent = next < 0 ? this.t(proposed ? 'all_unlocked' : 'unlocked') : this.t('remaining', { amount: this.money(milestones[next].amount - spend), reward: this.t(['free_next', 'gift_next', 'discount_next'][milestones[next].id]) });
  }
  renderRecommendations() {
    const list = this.querySelector('[data-rec-list]');
    if (!list) return;
    const families = new Set(Object.values(this.config.families).flatMap(f => f.split(',')));
    const ids = new Set(this.cart.items.map(i => i.product_id));
    const pulse = ids.has(10770401296721);
    const pool = this.config.products.filter(p => !ids.has(p.id) && !p.family.split(',').some(f => families.has(f)) && p.variants.some(v => v.available) && (p.id !== 10971471937873 || pulse));
    if (pulse) pool.sort((a, b) => Number(b.id === 10971471937873) - Number(a.id === 10971471937873));
    const used = new Set();
    const selected = pool.filter(p => { if (used.has(p.family)) return false; used.add(p.family); return true; }).slice(0, 3);
    list.replaceChildren();
    selected.forEach(p => {
      const variant = p.variants.find(v => v.available);
      const card = document.createElement('article'); card.className = 'shc-rec'; card.dataset.family = p.family;
      const imageLink = document.createElement('a'); imageLink.href = p.url;
      if (p.image) { const img = document.createElement('img'); img.src = p.image; img.alt = p.title; img.width = 74; img.height = 76; img.loading = 'lazy'; imageLink.append(img); }
      const info = document.createElement('div');
      const title = document.createElement('a'); title.className = 'shc-rec-title'; title.href = p.url; title.textContent = p.title;
      const price = document.createElement('div'); price.className = 'shc-rec-price'; price.textContent = this.money(variant.price); info.append(title, price);
      const action = document.createElement('button'); action.className = 'shc-secondary';
      action.type = 'button'; action.dataset.add = variant.id; action.textContent = this.t('add'); action.setAttribute('aria-label', this.t('add') + ': ' + p.title);
      card.append(imageLink, info, action); list.append(card);
    });
    this.querySelector('[data-recommendations]').hidden = !selected.length;
  }
  convert(amount, currency) {
    if (currency === this.cart.currency) return Math.round(amount * 100);
    const rates = { ...this.exchangeRates, [this.config.shopCurrency]: 1 };
    const active = window.Shopify?.currency;
    if (active?.active === this.cart.currency && Number(active.rate) > 0) rates[this.cart.currency] = Number(active.rate);
    if (!(rates[currency] > 0) || !(rates[this.cart.currency] > 0)) return null;
    return Math.round(amount / rates[currency] * rates[this.cart.currency] * 100);
  }
  calculateRates() {
    const country = this.querySelector('[data-country-select]')?.value;
    if (!country) return;
    this.destination = { country };
    const checkoutForm = this.querySelector('[data-cart-form]');
    if (checkoutForm) {
      const checkoutUrl = new URL(checkoutForm.getAttribute('action'), location.href);
      let checkoutCountry = country;
      try { checkoutCountry = new Intl.DisplayNames(['en'], { type: 'region' }).of(country); } catch {}
      checkoutUrl.searchParams.set('checkout[shipping_address][country]', checkoutCountry);
      checkoutForm.action = checkoutUrl.href;
    }
    const methods = new Map();
    for (const rule of this.shippingRules[country] || []) {
      const eligible = rule.conditions.every(condition => {
        if (condition.field !== 'TOTAL_PRICE') return false;
        const threshold = this.convert(condition.amount, condition.currency);
        if (threshold === null) return false;
        switch (condition.operator) {
          case 'GREATER_THAN_OR_EQUAL_TO': return this.cart.total_price >= threshold;
          case 'LESS_THAN_OR_EQUAL_TO': return this.cart.total_price <= threshold;
          case 'GREATER_THAN': return this.cart.total_price > threshold;
          case 'LESS_THAN': return this.cart.total_price < threshold;
          case 'EQUAL_TO': return this.cart.total_price === threshold;
          default: return false;
        }
      });
      if (!eligible) continue;
      const amount = this.convert(rule.price, rule.currency);
      if (amount === null) continue;
      const id = rule.id.split('?')[0];
      const rate = { id, name: rule.name, price: (amount / 100).toFixed(2), currency: this.cart.currency, estimated: rule.currency !== this.cart.currency, sourcePrice: rule.price, sourceCurrency: rule.currency };
      if (!methods.has(id) || Number(methods.get(id).price) > Number(rate.price)) methods.set(id, rate);
    }
    this.rates = [...methods.values()].sort((a, b) => Number(a.price) - Number(b.price));
    this.selectedRate = this.rates.find(r => r.id === this.selectedRate?.id) || this.rates[0] || null;
    this.renderRates();
    this.renderTotals();
  }
  dates(rate) {
    let range = (rate.delivery_range || []).filter(d => /^\d{4}-\d{2}-\d{2}/.test(d)).map(d => new Date(d.slice(0, 10) + 'T12:00:00Z'));
    if (!range.length && rate.delivery_date) range = [new Date(rate.delivery_date.slice(0, 10) + 'T12:00:00Z')];
    if (!range.length) {
      const config = this.markets[this.destination.country];
      if (!config) return '';
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
      const values = Object.fromEntries(parts.map(p => [p.type, p.value]));
      const start = new Date(`${values.year}-${values.month}-${values.day}T12:00:00Z`);
      const add = (date, days) => { const d = new Date(date); for (let n = 0; n < days;) { d.setUTCDate(d.getUTCDate() + 1); if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6) n++; } return d; };
      const dispatch = add(start, 1);
      range = [add(dispatch, config[3]), add(dispatch, config[4])];
    }
    const format = new Intl.DateTimeFormat(this.dataset.locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' });
    return range.filter(d => !Number.isNaN(d.valueOf())).map(d => format.format(d)).join(' – ');
  }
  renderRates() {
    const container = this.querySelector('[data-rates]'); container.replaceChildren();
    if (!this.rates.length) { container.textContent = this.t('no_rates'); return; }
    this.rates.forEach((rate, index) => {
      const label = document.createElement('label'); label.className = 'shc-rate';
      const input = document.createElement('input'); input.type = 'radio'; input.name = 'shipping-rate'; input.value = index; input.checked = index === 0;
      const text = document.createElement('span'); const name = document.createElement('strong'); name.textContent = /^(standard|estándar)$/i.test(rate.presentment_name || rate.name) ? this.t('standard') : rate.presentment_name || rate.name; text.append(name);
      const dates = this.dates(rate);
      if (dates) {
        const eta = document.createElement('small');
        const date = document.createElement('span'); date.className = 'shc-rate-eta'; date.textContent = dates;
        const [prefix, suffix = ''] = this.t('eta').split('{dates}');
        eta.append(prefix, date, suffix); text.append(eta);
      }
      if (rate.estimated) { const note = document.createElement('small'); note.textContent = this.t('exchange_note', { amount: new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: rate.sourceCurrency }).format(Number(rate.sourcePrice)) }); text.append(note); }
      const price = document.createElement('span'); price.className = 'shc-rate-price'; price.textContent = Number(rate.price) === 0 ? this.t('free') : this.money(Math.round(Number(rate.price) * 100));
      label.append(input, text, price); container.append(label);
    });
  }
  renderTotals() {
    const rate = this.selectedRate;
    const total = this.cart.total_price + (rate ? Math.round(Number(rate.price) * 100) : 0);
    this.querySelector('[data-shipping-total]').textContent = rate ? Number(rate.price) === 0 ? this.t('free') : this.money(Math.round(Number(rate.price) * 100)) : this.t('to_calculate');
    for (const selector of ['[data-grand-total]', '[data-mobile-total]']) this.querySelector(selector).textContent = this.money(total);
    for (const selector of ['[data-total-label]', '[data-mobile-label]']) this.querySelector(selector).textContent = this.t(rate ? rate.estimated ? 'estimated_total' : 'total' : 'subtotal');
    this.querySelector('[data-total-note]').textContent = this.t(rate ? 'total_note' : 'total_help');
  }
});


